import { useCallback, useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/utils/Functions";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsiblePanel,
} from "@/components/ui/collapsible";
import type { NylasMessage } from "@equaliq/api-model-v2";

interface MessageComponentProps {
  message: NylasMessage;
  /** If true, message starts expanded. Defaults to false (collapsed). */
  defaultExpanded?: boolean;
}

export function MessageComponent({
  message,
  defaultExpanded = false,
}: MessageComponentProps) {
  const [isOpen, setIsOpen] = useState(defaultExpanded);
  const senderName =
    message.from?.[0]?.name || message.from?.[0]?.email || "Unknown";
  const senderEmail = message.from?.[0]?.email || "";
  const formattedDate = new Date(message.date * 1000).toLocaleString();

  const emailContent = message.body || message.snippet || "";

  // Auto-resize iframe to fit content, avoiding a fixed height with scrollbars
  const handleIframeLoad = useCallback(
    (e: React.SyntheticEvent<HTMLIFrameElement>) => {
      const iframe = e.currentTarget;
      try {
        const doc = iframe.contentDocument;
        if (doc?.body) {
          iframe.style.height = `${doc.body.scrollHeight + 16}px`;
        }
      } catch {
        // Cross-origin restriction fallback — keep default min-height
      }
    },
    []
  );

  // Wrap email HTML with base styles for readable rendering in the sandboxed iframe
  const iframeSrcDoc = emailContent
    ? `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body {
      margin: 0;
      padding: 8px;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      font-size: 14px;
      line-height: 1.5;
      color: #1a1a1a;
      background-color: white;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }
    img { max-width: 100%; height: auto; }
    a { color: #2563eb; }
    pre, code { white-space: pre-wrap; }
    table { max-width: 100%; }
  </style>
</head>
<body>${emailContent}</body>
</html>`
    : "";

  // Show snippet as preview text in collapsed state
  const snippetPreview = message.snippet
    ? message.snippet.length > 100
      ? message.snippet.slice(0, 100) + "..."
      : message.snippet
    : "";

  return (
    <div
      className={cn(
        "rounded-lg border border-border/50",
        message.unread && "bg-accent/20 border-primary/20"
      )}
    >
      <Collapsible defaultOpen={defaultExpanded} onOpenChange={setIsOpen}>
        {/* Collapsed header — always visible */}
        <CollapsibleTrigger className="flex items-center gap-2 w-full text-left p-3 group">
          <ChevronDown className="size-4 text-muted-foreground shrink-0 transition-transform group-data-[panel-open]:rotate-180" />
          <div className="flex-1 min-w-0 flex items-baseline gap-2">
            <span className="text-sm font-medium truncate">{senderName}</span>
            {senderEmail && senderName !== senderEmail && (
              <span className="text-xs text-muted-foreground truncate hidden sm:inline">
                &lt;{senderEmail}&gt;
              </span>
            )}
          </div>
          <span className="text-xs text-muted-foreground shrink-0">
            {formattedDate}
          </span>
        </CollapsibleTrigger>

        {/* Snippet preview — visible only when collapsed */}
        {!isOpen && snippetPreview && (
          <p className="px-3 pb-2 text-xs text-muted-foreground truncate">
            {snippetPreview}
          </p>
        )}

        {/* Expanded content */}
        <CollapsiblePanel>
          <div className="px-3 pb-3">
            {/* Subject */}
            {message.subject && (
              <p className="text-sm font-medium mb-1">{message.subject}</p>
            )}

            {/* Recipients */}
            {message.to && message.to.length > 0 && (
              <p className="text-xs text-muted-foreground mb-1">
                To: {message.to.map((t) => t.name || t.email).join(", ")}
              </p>
            )}
            {message.cc && message.cc.length > 0 && (
              <p className="text-xs text-muted-foreground mb-1">
                Cc: {message.cc.map((t) => t.name || t.email).join(", ")}
              </p>
            )}
            {message.bcc && message.bcc.length > 0 && (
              <p className="text-xs text-muted-foreground mb-2">
                Bcc: {message.bcc.map((t) => t.name || t.email).join(", ")}
              </p>
            )}

            {/* Message body — rendered in sandboxed iframe */}
            {emailContent ? (
              <div className="mt-2 border-t border-border/30 pt-2">
                <iframe
                  srcDoc={iframeSrcDoc}
                  className="w-full min-h-[200px] border-0"
                  sandbox="allow-same-origin"
                  referrerPolicy="no-referrer"
                  title="Email content"
                  onLoad={handleIframeLoad}
                />
              </div>
            ) : null}
          </div>
        </CollapsiblePanel>
      </Collapsible>
    </div>
  );
}
